import React, { useEffect, useState } from 'react';
import axios from 'axios';
import API_BASE from './apiBase';
import Swal from 'sweetalert2';
import '../css/App.css';
import 'font-awesome/css/font-awesome.min.css';

function TenantsPage({ token, user }) {
  const [tenants, setTenants] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [name, setName] = useState('');
  const [managerEmail, setManagerEmail] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [plan, setPlan] = useState('Subscription');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [limit, setLimit] = useState('');
  const [consentBannerLimit, setConsentBannerLimit] = useState('');
  const [cookieBannerLimit, setCookieBannerLimit] = useState('');
  const [sectorFiled, setSectorFiled] = useState('');
  const [googleClientId, setGoogleClientId] = useState('');
  const [msClientId, setMsClientId] = useState('');
  const [result, setResult] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [trialConsents, setTrialConsents] = useState([]);
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [loadingTrials, setLoadingTrials] = useState(false);
  const [trialPage, setTrialPage] = useState(0);
  const [trialDays, setTrialDays] = useState(7);
  const TRIAL_PAGE_SIZE = 5;
  const [trialTotal, setTrialTotal] = useState(0);
  const [pendingTrialId, setPendingTrialId] = useState(null);

  // --- Edit and Delete Modal State ---
  const [editTenant, setEditTenant] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [deleteTenantId, setDeleteTenantId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedTenantIds, setSelectedTenantIds] = useState([]);

  const fetchTenants = async () => {
    try {
      const res = await axios.get(`${API_BASE}/admin/tenants`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTenants(res.data);
    } catch (err) {
      setResult('Failed to fetch tenants');
    }
  };

  const fetchTrialConsents = async (page = 0) => {
    setLoadingTrials(true);
    try {
      const offset = page * TRIAL_PAGE_SIZE;
      let url = `${API_BASE}/admin/trial-consents?limit=${TRIAL_PAGE_SIZE}&offset=${offset}`;
      if (trialDays !== 'all') {
        url = `${API_BASE}/admin/trial-consents?days=${trialDays}&limit=${TRIAL_PAGE_SIZE}&offset=${offset}`;
      }
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = res.data || {};
      if (Array.isArray(data)) {
        // backward compatibility: array returned
        setTrialConsents(data);
        setTrialTotal(data.length);
      } else {
        setTrialConsents(Array.isArray(data.items) ? data.items : []);
        setTrialTotal(typeof data.total === 'number' ? data.total : (Array.isArray(data.items) ? data.items.length : 0));
      }
    } catch (err) {
      setTrialConsents([]);
      setTrialTotal(0);
    } finally {
      setLoadingTrials(false);
    }
  };

  // Format an ISO datetime/string into India Standard Time (Asia/Kolkata)
  const formatToIST = (isoString) => {
    if (!isoString) return '';
    try {
      const d = new Date(isoString);
      return new Intl.DateTimeFormat('en-GB', {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false,
        timeZone: 'Asia/Kolkata'
      }).format(d);
    } catch (e) {
      return isoString;
    }
  };

  // Map plan values to user-friendly labels
  const getPlanLabel = (plan) => {
    if (!plan) return '-';
    if (plan === 'Subscription') return 'Subscription';
    if (plan === 'Pay as You Go') return 'Pay as you go';
    if (plan === 'Trial' || plan === 'Free') return 'Free (7d trial)';
    return plan;
  };

  const getTenantStatusIcon = (tenant) => {
    const status = (tenant.status || '').toLowerCase();
    const hasLogin = Boolean(tenant.has_active_login);
    if (status === 'inactive') {
      return {
        icon: 'fa-times-circle',
        color: '#6b7280',
        title: `Inactive tenant${hasLogin ? ' (active login detected)' : ''} — click to activate`,
        clickable: true,
      };
    }
    if (status === 'active') {
      return {
        icon: 'fa-check-circle',
        color: '#2e7d32',
        title: `Active tenant${hasLogin ? ' (active login detected)' : ''} — click to deactivate`,
        clickable: true,
      };
    }
    return {
      icon: 'fa-question-circle',
      color: '#6b7280',
      title: `Tenant status unknown${hasLogin ? ' (active login detected)' : ''} — click to activate`,
      clickable: true,
    };
  };

  const toggleTenantStatus = async (tenant) => {
    const currentStatus = (tenant.status || '').toLowerCase();
    const newStatus = currentStatus === 'inactive' ? 'active' : 'inactive';
    try {
      await axios.put(`${API_BASE}/admin/tenant/${tenant.id}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTenants((prevTenants) => prevTenants.map((t) => t.id === tenant.id ? { ...t, status: newStatus } : t));
      await fetchTenants();
      Swal.fire({ icon: 'success', title: 'Success', text: `Tenant marked ${newStatus}.` });
    } catch (err) {
      let msg = 'Failed to update tenant status';
      if (err.response?.data?.detail) {
        msg = typeof err.response.data.detail === 'string' ? err.response.data.detail : JSON.stringify(err.response.data.detail);
      } else if (err.message) {
        msg = err.message;
      }
      Swal.fire({ icon: 'error', title: 'Error', text: msg });
    }
  };

  useEffect(() => {
    fetchTenants();
    fetchTrialConsents(0);
    // eslint-disable-next-line
  }, []);

  const handleAddTenant = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setResult('');
    // client-side duplicate checks
    try {
      const nameTrim = (name || '').trim().toLowerCase();
      const emailTrim = (managerEmail || '').trim().toLowerCase();
      const orgIdNum = organizationId ? String(organizationId).trim() : '';
      if (nameTrim && tenants.some(t => (t.name || '').trim().toLowerCase() === nameTrim)) {
        throw new Error('A tenant with that name already exists.');
      }
      if (emailTrim && tenants.some(t => (t.manager_email || '').trim().toLowerCase() === emailTrim)) {
        throw new Error('That manager email is already assigned to another tenant.');
      }
      if (orgIdNum && tenants.some(t => String(t.organization_id || t.organizationId || '').trim() === orgIdNum)) {
        throw new Error('That Organization ID is already in use by another tenant.');
      }
    } catch (dupErr) {
      setSubmitting(false);
      setResult(dupErr.message || 'Duplicate tenant data');
      try { await Swal.fire({ icon: 'warning', title: 'Duplicate', text: dupErr.message }); } catch (e) { }
      return;
    }
    try {
      const res = await axios.post(`${API_BASE}/admin/tenant`, {
        name,
        manager_email: managerEmail,
        organization_id: Number(organizationId),
        plan,
        start_date: startDate,
        end_date: endDate,
        limit: Number(limit),
        consent_banner_limit: consentBannerLimit ? Number(consentBannerLimit) : Number(limit),
        cookie_banner_limit: cookieBannerLimit ? Number(cookieBannerLimit) : Number(limit),
        sector_filed: sectorFiled,
        google_client_id: googleClientId,
        ms_client_id: msClientId,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowAdd(false);
      setName('');
      setManagerEmail('');
      setPlan('Subscription');
      setStartDate('');
      setEndDate('');
      setLimit('');
      setConsentBannerLimit('');
      setCookieBannerLimit('');
      setSectorFiled('');
      setGoogleClientId('');
      setMsClientId('');
      fetchTenants();
      // Swal .fire({ icon: 'success', title: 'Success', text: res.data.message || 'Tenant added successfully!' });
      // If SMTP/email failed return includes manager_password; show it to admin as fallback
      const resp = res.data || {};
      if (resp.emailed === false && resp.manager_password) {
        await Swal.fire({
          icon: 'success',
          title: 'Tenant added',
          html: `<div>Tenant created successfully.<br><br><strong>Manager password:</strong> <code>${resp.manager_password}</code><br><br>Email delivery failed; copy this password and send securely to the manager.</div>`,
          customClass: { popup: 'swal-popup-custom' }
        });
      } else {
        Swal.fire({ icon: 'success', title: 'Success', text: res.data.message || 'Tenant added successfully!' });
      }
      // If backend returned a tenant id, set it as the active tenant so other pages can use it
      try {
        const newTenantId = res.data?.tenant_id || resp.tenant_id || null;
        if (newTenantId) {
          try { localStorage.setItem('tenant_id', String(newTenantId)); } catch (e) { }
          try {
            if (!window.USER) window.USER = {};
            window.USER.tenant_id = Number(newTenantId);
            if (!window.__USER__) window.__USER__ = {};
            window.__USER__.tenant_id = Number(newTenantId);
          } catch (e) { }
          try { window.dispatchEvent(new CustomEvent('tenantChanged', { detail: { tenant_id: Number(newTenantId), tenant_name: name } })); } catch (e) { }
        }
        // If we were creating this tenant from a trial request, mark trial as approved
        try {
          if (pendingTrialId && newTenantId) {
            await axios.post(`${API_BASE}/admin/trial-consents/${pendingTrialId}/approve?tenant_id=${newTenantId}`, {}, {
              headers: { Authorization: `Bearer ${token}` }
            });
            setPendingTrialId(null);
            fetchTrialConsents(trialPage);
          }
        } catch (e) {
          // ignore approval errors but show a warning
          console.warn('Failed to auto-approve trial', e);
        }
      } catch (e) { }
    } catch (err) {
      let msg = 'Failed to add tenant';
      if (err.response?.data?.detail) {
        msg = typeof err.response.data.detail === 'string'
          ? err.response.data.detail
          : JSON.stringify(err.response.data.detail);
      } else if (err.message) {
        msg = err.message;
      }
      setResult(msg);
      Swal.fire({ icon: 'error', title: 'Error', text: msg });
    } finally {
      setSubmitting(false);
    }
  };

  const approveTrial = async (trialId, tenantId = null) => {
    try {
      const q = tenantId ? `?tenant_id=${tenantId}` : '';
      await axios.post(`${API_BASE}/admin/trial-consents/${trialId}/approve${q}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTrialConsents(trialPage);
      Swal.fire({ icon: 'success', title: 'Approved', text: 'Trial request approved.' });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to approve trial request.' });
    }
  };

  const rejectTrial = async (trialId) => {
    try {
      const { value: reason } = await Swal.fire({
        title: 'Reject trial request',
        input: 'textarea',
        inputPlaceholder: 'Optional reason for rejection (will be emailed to requester)',
        showCancelButton: true
      });
      if (reason === undefined) return; // cancelled
      if (!window.confirm('Reject this trial request and notify the requester?')) return;
      const payload = reason ? { reason } : {};
      const res = await axios.post(`${API_BASE}/admin/trial-consents/${trialId}/reject`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = res.data || {};
      fetchTrialConsents(trialPage);
      if (data.emailed === false) {
        Swal.fire({ icon: 'warning', title: 'Rejected', text: 'Trial request rejected. Email delivery failed.' });
      } else {
        Swal.fire({ icon: 'success', title: 'Rejected', text: 'Trial request rejected and requester notified.' });
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to reject trial request.' });
    }
  };

  // --- Edit Modal Handlers ---
  const openEditModal = (tenant) => {
    setEditTenant({ ...tenant });
    setShowEdit(true);
  };
  const closeEditModal = () => {
    setShowEdit(false);
    setEditTenant(null);
    setResult('');
  };
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditTenant((prev) => ({ ...prev, [name]: value }));
  };
  const handleEditSave = async (e) => {
    e.preventDefault();
    setResult('');
    try {
      // Prevent changing to a duplicate name/email/org id
      const nameTrim = (editTenant.name || '').trim().toLowerCase();
      const emailTrim = (editTenant.manager_email || '').trim().toLowerCase();
      const orgIdNum = editTenant.organization_id ? String(editTenant.organization_id).trim() : '';
      if (nameTrim && tenants.some(t => t.id !== editTenant.id && (t.name || '').trim().toLowerCase() === nameTrim)) {
        throw new Error('A tenant with that name already exists.');
      }
      if (emailTrim && tenants.some(t => t.id !== editTenant.id && (t.manager_email || '').trim().toLowerCase() === emailTrim)) {
        throw new Error('That manager email is already assigned to another tenant.');
      }
      if (orgIdNum && tenants.some(t => t.id !== editTenant.id && String(t.organization_id || t.organizationId || '').trim() === orgIdNum)) {
        throw new Error('That Organization ID is already in use by another tenant.');
      }
      // Update tenant
      await axios.put(`${API_BASE}/admin/tenant/${editTenant.id}`, {
        name: editTenant.name,
        domain: editTenant.domain,
        status: editTenant.status,
        billing_info: editTenant.billing_info,
        manager_email: editTenant.manager_email,
        sector_filed: editTenant.sector_filed,
        google_client_id: editTenant.google_client_id,
        ms_client_id: editTenant.ms_client_id,
        consent_banner_limit: editTenant.consent_banner_limit !== undefined && editTenant.consent_banner_limit !== '' ? Number(editTenant.consent_banner_limit) : null,
        cookie_banner_limit: editTenant.cookie_banner_limit !== undefined && editTenant.cookie_banner_limit !== '' ? Number(editTenant.cookie_banner_limit) : null,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Update subscription
      await axios.put(`${API_BASE}/admin/subscription/${editTenant.id}`, {
        plan: editTenant.plan,
        start_date: editTenant.start_date,
        end_date: editTenant.end_date,
        limit: editTenant.limit,
        consent_banner_limit: editTenant.consent_banner_limit !== undefined && editTenant.consent_banner_limit !== '' ? Number(editTenant.consent_banner_limit) : null,
        cookie_banner_limit: editTenant.cookie_banner_limit !== undefined && editTenant.cookie_banner_limit !== '' ? Number(editTenant.cookie_banner_limit) : null,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      closeEditModal();
      fetchTenants();
      Swal.fire({ icon: 'success', title: 'Success', text: 'Tenant updated successfully!' });
    } catch (err) {
      let msg = 'Failed to update tenant';
      if (err.message && err.message.indexOf('already') !== -1) {
        msg = err.message;
      } else if (err.response?.data?.detail) {
        msg = typeof err.response.data.detail === 'string'
          ? err.response.data.detail
          : JSON.stringify(err.response.data.detail);
      } else if (err.message) {
        msg = err.message;
      }
      setResult(msg);
      Swal.fire({ icon: 'error', title: 'Error', text: msg });
    }
  };

  // --- Delete Modal Handlers ---
  const openDeleteConfirm = (tenant) => {
    // const used = Number(tenant.used || 0);
    // if(used > 0) {
    if (tenant.has_active_login) {
      Swal.fire({ icon: 'warning', title: 'Cannot Delete', text: 'Cannot delete tenant while a user is still logged in. Please wait until the session expires or the user logs out.' });
      return;
    }
    setDeleteTenantId(tenant.id);
    setShowDeleteConfirm(true);
  };
  const closeDeleteConfirm = () => {
    setShowDeleteConfirm(false);
    setDeleteTenantId(null);
    setResult('');
  };
  const handleDeleteTenant = async () => {
    setResult('');
    try {
      await axios.delete(`${API_BASE}/admin/tenant/${deleteTenantId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      closeDeleteConfirm();
      fetchTenants();
      Swal.fire({ icon: 'success', title: 'Deleted', text: 'Tenant deleted successfully!' });
    } catch (err) {
      let msg = 'Failed to delete tenant';
      if (err.response?.data?.detail) {
        msg = typeof err.response.data.detail === 'string'
          ? err.response.data.detail
          : JSON.stringify(err.response.data.detail);
      } else if (err.message) {
        msg = err.message;
      }
      setResult(msg);
      Swal.fire({ icon: 'error', title: 'Error', text: msg });
    }
  };

  const getSelectionId = (tenantId) => String(tenantId);

  const toggleTenantSelection = (tenantId) => {
    const selectionId = getSelectionId(tenantId);
    setSelectedTenantIds(prev => prev.includes(selectionId)
      ? prev.filter(id => id !== selectionId)
      : [...prev, selectionId]);
  };

  const handleBulkDelete = async () => {
    if (!selectedTenantIds.length) {
      Swal.fire({ icon: 'warning', title: 'No selection', text: 'Select at least one tenant to delete.' });
      return;
    }

    const confirm = await Swal.fire({
      title: 'Delete selected tenants?',
      text: 'Selected tenants and their related data will be removed. Tenants with active logins will be skipped.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
    });

    if (!confirm.isConfirmed) return;

    setResult('');
    try {
      const res = await axios.post(`${API_BASE}/admin/tenant/bulk-delete`, {
        tenant_ids: selectedTenantIds.map(id => Number(id)),
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = res.data || {};
      setSelectedTenantIds([]);
      fetchTenants();
      const skipped = Array.isArray(data.skipped_tenants) && data.skipped_tenants.length ? ` Skipped: ${data.skipped_tenants.join(', ')}.` : '';
      Swal.fire({
        icon: 'success',
        title: 'Bulk delete completed',
        text: `${data.message || 'Delete complete.'}${skipped}`,
      });
    } catch (err) {
      let msg = 'Failed to delete selected tenants';
      if (err.response?.data?.detail) {
        msg = typeof err.response.data.detail === 'string'
          ? err.response.data.detail
          : JSON.stringify(err.response.data.detail);
      } else if (err.message) {
        msg = err.message;
      }
      setResult(msg);
      Swal.fire({ icon: 'error', title: 'Error', text: msg });
    }
  };

  const selectTenant = (tenant) => {
    try {
      localStorage.setItem('tenant_id', String(tenant.id));
    } catch (e) { }
    try {
      if (window && window.USER) window.USER.tenant_id = tenant.id;
      if (window && window.__USER__) window.__USER__.tenant_id = tenant.id;
    } catch (e) { }
    try {
      window.dispatchEvent(new CustomEvent('tenantChanged', { detail: { tenant_id: tenant.id, tenant_name: tenant.name } }));
    } catch (e) { }
    Swal.fire({ icon: 'success', title: 'Tenant Selected', text: `Selected tenant: ${tenant.name} (ID ${tenant.id})` });
  };

  const handleResendEmail = async (tenant) => {
    setResult('');
    try {
      const res = await axios.post(`${API_BASE}/admin/tenant/${tenant.id}/resend-manager-email`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = res.data || {};
      if (data.emailed === false && data.manager_password) {
        await Swal.fire({
          icon: 'success',
          title: 'Email delivery failed',
          html: `<div>New password generated:<br><br><strong>Password:</strong> <code>${data.manager_password}</code><br><br>Email delivery failed; copy this password and send securely to the manager.</div>`,
          customClass: { popup: 'swal-popup-custom' }
        });
      } else {
        Swal.fire({ icon: 'success', title: 'Email Sent', text: 'New manager password emailed successfully.' });
      }
    } catch (err) {
      let msg = 'Failed to resend manager email';
      if (err.response?.data?.detail) {
        msg = typeof err.response.data.detail === 'string'
          ? err.response.data.detail
          : JSON.stringify(err.response.data.detail);
      } else if (err.message) {
        msg = err.message;
      }
      setResult(msg);
      Swal.fire({ icon: 'error', title: 'Error', text: msg });
    }
  };

  const handlePurchase = async () => {
    try {
      const payload = {
        amount: 5000, // cents: $50.00
        currency: 'usd',
        item_name: 'ConsentData License (1 year)',
        success_url: window.location.origin + '/billing/success',
        cancel_url: window.location.origin + '/billing/cancel'
      };
      const res = await axios.post(`${API_BASE}/payments/create-session`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const url = res.data && res.data.url;
      if (url) {
        window.location = url;
      } else {
        Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to create payment session.' });
      }
    } catch (err) {
      let msg = 'Failed to start checkout';
      if (err.response?.data?.detail) msg = err.response.data.detail;
      Swal.fire({ icon: 'error', title: 'Error', text: msg });
    }
  };

  const annualCount = tenants.filter(t => t.plan === 'Subscription').length;
  const paygCount = tenants.filter(t => t.plan === 'Pay as You Go').length;

  return (
    <div className="tenants-page">
      <div className="tenants-header">
        <div className="tenants-cards">
          <div className="tenant-card total">
            <div className="card-title">Total Tenants</div>
            <div className="card-value">{tenants.length}</div>
          </div>
          <div className="tenant-card annual">
            <div className="card-title">Annual</div>
            <div className="card-value">{annualCount}</div>
          </div>
          <div className="tenant-card payg">
            <div className="card-title">Pay as you go</div>
            <div className="card-value">{paygCount}</div>
          </div>
          <div className="tenant-card trial" onClick={() => { setShowTrialModal(true); setTrialPage(0); fetchTrialConsents(0); }} style={{ cursor: 'pointer' }} title="View trial requests">
            <div className="card-title">Trial Requests (7d)</div>
            <div className="card-value">{trialTotal}</div>
          </div>
        </div>
        {user && user.role === 'admin' && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="add-tenant-btn" onClick={() => setShowAdd(true)} title="Add Tenant">
              <span role="img" aria-label="add">➕</span>
            </button>
            <button className="cancel-btn" onClick={handleBulkDelete} title="Delete Selected" disabled={!selectedTenantIds.length} style={{ opacity: selectedTenantIds.length ? 1 : 0.5 }}>
              🗑️ Delete Selected
            </button>
          </div>
        )}
      </div>
      {showAdd && user && user.role === 'admin' && (
        <div className="add-tenant-modal">
          <form className="add-tenant-form add-tenant-form-large" onSubmit={handleAddTenant}>
            <h2 style={{ marginBottom: '1.2rem', color: '#2a3a5e' }}>Add Tenant</h2>
            <div className="form-row">
              <div className="form-group">
                <label>Tenant Name</label>
                <input placeholder="Tenant Name" value={name} onChange={e => setName(e.target.value)} required />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input placeholder="Manager Email" value={managerEmail} onChange={e => setManagerEmail(e.target.value)} required />
              </div>
              <div className="form-group">
                <label>Organization ID</label>
                <input type="number" placeholder="Organization ID (unique)" value={organizationId} onChange={e => setOrganizationId(e.target.value)} required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Google OAuth Client ID</label>
                <input placeholder="Tenant Google Client ID" value={googleClientId} onChange={e => setGoogleClientId(e.target.value)} />
              </div>
              <div className="form-group">
                <label>Microsoft OAuth Client ID</label>
                <input placeholder="Tenant Microsoft Client ID" value={msClientId} onChange={e => setMsClientId(e.target.value)} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Sector / Industry</label>
                <input placeholder="e.g. Bank, Hotel, Hospital" value={sectorFiled} onChange={e => setSectorFiled(e.target.value)} />
              </div>
              <div className="form-group">
                <label>Plan</label>
                <select value={plan} onChange={e => setPlan(e.target.value)}>
                  <option value="Trial">Free Trial (7 days)</option>
                  <option value="Subscription">Subscription</option>
                  <option value="Pay as You Go">Pay as You Go</option>
                </select>
              </div>
              <div className="form-group">
                <label>Start Date</label>
                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} required />
              </div>
              <div className="form-group">
                <label>End Date</label>
                <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} required />
              </div>
              <div className="form-group">
                <label>Limit (Users)</label>
                <input type="number" min="1" placeholder="User Limit" value={limit} onChange={e => setLimit(e.target.value)} required />
              </div>
              <div className="form-group">
                <label>Consent Banner Limit</label>
                <input type="number" min="1" placeholder="Consent Banner Limit" value={consentBannerLimit} onChange={e => setConsentBannerLimit(e.target.value)} />
              </div>
              <div className="form-group">
                <label>Cookie Banner Limit</label>
                <input type="number" min="1" placeholder="Cookie Banner Limit" value={cookieBannerLimit} onChange={e => setCookieBannerLimit(e.target.value)} />
              </div>
            </div>
            <div className="modal-actions">
              <button type="submit" className="save-btn" title="Save" disabled={submitting} style={submitting ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}>
                <span role="img" aria-label="save">{submitting ? '⏳' : '💾'}</span>
              </button>
              <button type="button" className="cancel-btn" onClick={() => setShowAdd(false)} title="Cancel" disabled={submitting}>
                <span role="img" aria-label="cancel">❌</span>
              </button>
            </div>
            {result && <div className="error-message">{result}</div>}
          </form>
        </div>
      )}
      {/* Edit Tenant Modal */}
      {showEdit && editTenant && (
        <div className="add-tenant-modal">
          <form className="add-tenant-form add-tenant-form-large" onSubmit={handleEditSave}>
            <h2 style={{ marginBottom: '1.2rem', color: '#2a3a5e' }}>Edit Tenant</h2>
            <div className="form-row">
              <div className="form-group">
                <label>Tenant Name</label>
                <input name="name" value={editTenant.name || ''} onChange={handleEditChange} required />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input name="manager_email" value={editTenant.manager_email || ''} onChange={handleEditChange} required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Google OAuth Client ID</label>
                <input name="google_client_id" placeholder="Tenant Google Client ID" value={editTenant.google_client_id || ''} onChange={handleEditChange} />
              </div>
              <div className="form-group">
                <label>Microsoft OAuth Client ID</label>
                <input name="ms_client_id" placeholder="Tenant Microsoft Client ID" value={editTenant.ms_client_id || ''} onChange={handleEditChange} />
              </div>
              <div className="form-group">
                <label>Sector / Industry</label>
                <input name="sector_filed" placeholder="e.g. Bank, Hotel, Hospital" value={editTenant.sector_filed || ''} onChange={handleEditChange} />
              </div>
              <div className="form-group">
                <label>Plan</label>
                <select name="plan" value={editTenant.plan || 'Subscription'} onChange={handleEditChange}>
                  <option value="Subscription">Subscription</option>
                  <option value="Pay as You Go">Pay as You Go</option>
                  <option value="Trial">Free Trial (7 days)</option>
                </select>
              </div>
              <div className="form-group">
                <label>Start Date</label>
                <input type="date" name="start_date" value={editTenant.start_date ? editTenant.start_date.slice(0, 10) : ''} onChange={handleEditChange} required />
              </div>
              <div className="form-group">
                <label>End Date</label>
                <input type="date" name="end_date" value={editTenant.end_date ? editTenant.end_date.slice(0, 10) : ''} onChange={handleEditChange} required />
              </div>
              <div className="form-group">
                <label>Limit (Users)</label>
                <input type="number" name="limit" min="1" value={editTenant.limit || ''} onChange={handleEditChange} required />
              </div>
              <div className="form-group">
                <label>Consent Banner Limit</label>
                <input type="number" name="consent_banner_limit" min="1" placeholder="Consent Banner Limit" value={editTenant.consent_banner_limit ?? ''} onChange={handleEditChange} />
              </div>
              <div className="form-group">
                <label>Cookie Banner Limit</label>
                <input type="number" name="cookie_banner_limit" min="1" placeholder="Cookie Banner Limit" value={editTenant.cookie_banner_limit ?? ''} onChange={handleEditChange} />
              </div>
            </div>
            <div className="modal-actions">
              <button type="submit" className="save-btn" title="Save">
                <span role="img" aria-label="save">💾</span>
              </button>
              <button type="button" className="cancel-btn" onClick={closeEditModal} title="Cancel">
                <span role="img" aria-label="cancel">❌</span>
              </button>
            </div>
            {result && <div className="error-message">{result}</div>}
          </form>
        </div>
      )}
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="add-tenant-modal">
          <div className="add-tenant-form add-tenant-form-large" style={{ textAlign: 'center' }}>
            <h2 style={{ color: '#e53935' }}>Confirm Deletion</h2>
            <p>Are you sure you want to delete this tenant and all related data?</p>
            <div className="modal-actions" style={{ justifyContent: 'center' }}>
              <button className="save-btn" onClick={handleDeleteTenant} title="Confirm Delete">
                <span role="img" aria-label="delete">🗑️</span>
              </button>
              <button className="cancel-btn" onClick={closeDeleteConfirm} title="Cancel">
                <span role="img" aria-label="cancel">❌</span>
              </button>
            </div>
            {result && <div className="error-message">{result}</div>}
          </div>
        </div>
      )}

      {showTrialModal && (
        <div className="add-tenant-modal">
          <div className="add-tenant-form add-tenant-form-large" style={{ maxWidth: 800 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
              <h2 style={{ margin: 0, color: '#2a3a5e' }}>Trial Requests ({trialDays === 'all' ? 'all' : `last ${trialDays} days`})</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <select value={trialDays} onChange={e => setTrialDays(e.target.value === 'all' ? 'all' : Number(e.target.value))} style={{ padding: '0.35rem', borderRadius: 4 }} title="Select days">
                  <option value={7}>Last 7 days</option>
                  <option value={30}>Last 30 days</option>
                  <option value={90}>Last 90 days</option>
                  <option value={365}>Last 365 days</option>
                  <option value="all">All</option>
                </select>
                <button className="save-btn" style={{ padding: '0.4rem 0.6rem', fontSize: '0.9rem' }} onClick={() => { setTrialPage(0); fetchTrialConsents(0); }} title="Filter list">
                  <i className="fa fa-filter" aria-hidden="true" style={{ marginRight: 6 }}></i>Filter
                </button>
                <button className="save-btn" style={{ padding: '0.4rem 0.6rem', fontSize: '0.9rem' }} onClick={() => fetchTrialConsents(trialPage)} title="Refresh list">
                  <i className="fa fa-refresh" aria-hidden="true" style={{ marginRight: 6 }}></i>Refresh
                </button>
              </div>
            </div>
            <div style={{ maxHeight: '45vh', overflow: 'auto', paddingRight: 8 }}>
              {loadingTrials ? (
                <div>Loading...</div>
              ) : (!trialConsents || trialConsents.length === 0) ? (
                <div>No trial requests in the last 7 days.</div>
              ) : (
                <>
                  <div style={{ marginBottom: 8, fontSize: '0.9rem', color: '#444' }}>Showing {Math.min(TRIAL_PAGE_SIZE, trialConsents.length)} of {trialTotal}</div>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {trialConsents.map(tc => (
                      <li key={tc.id} style={{ borderBottom: '1px solid #eee', padding: '10px 0' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                          <div>
                            <strong>{tc.name || '(no name)'}</strong> — {tc.email}
                            {tc.organization ? <div style={{ fontSize: '0.9rem', color: '#444' }}>Org: {tc.organization}</div> : null}
                            <div style={{ marginTop: 6 }}>
                              <span style={{ fontSize: '0.8rem', padding: '4px 8px', borderRadius: 8, background: tc.status === 'approved' ? '#e6ffed' : '#fff8e6', color: tc.status === 'approved' ? '#107a32' : '#a16207' }}>{tc.status || 'pending'}</span>
                            </div>
                          </div>
                          <div style={{ fontSize: '0.8rem', color: '#666' }}>{formatToIST(tc.created_at)}</div>
                        </div>
                        {tc.phone ? <div style={{ fontSize: '0.9rem', color: '#444' }}>Phone: {tc.phone}</div> : null}
                        {tc.message ? <div style={{ marginTop: 6, color: '#333' }}>{tc.message}</div> : null}
                        <div style={{ marginTop: 8 }}>
                          {user && user.role === 'admin' && tc.status !== 'approved' && (
                            <>
                              <button className="tiny-btn primary" style={{ marginRight: 8 }} onClick={() => {
                                // open Add Tenant modal prefilled from trial
                                setShowAdd(true);
                                setName(tc.organization || tc.name || '');
                                setManagerEmail(tc.email || '');
                                setOrganizationId('');
                                setPendingTrialId(tc.id);
                                // default to a 7-day free trial (so tenant isn't treated as paid)
                                setPlan('Trial');
                                try {
                                  const today = new Date();
                                  setStartDate(today.toISOString().slice(0, 10));
                                  const end = new Date(today);
                                  end.setDate(end.getDate() + 7);
                                  setEndDate(end.toISOString().slice(0, 10));
                                } catch (e) { }
                                setLimit(0);
                              }} title="Create Tenant">
                                <i className="fa fa-user-plus" aria-hidden="true" style={{ marginRight: 6 }}></i>
                                Create Tenant
                              </button>
                              <button className="tiny-btn ghost" onClick={() => approveTrial(tc.id)} style={{ marginRight: 8 }} title="Approve">
                                <i className="fa fa-check" aria-hidden="true" style={{ marginRight: 6 }}></i>
                                Approve
                              </button>
                              <button className="tiny-btn danger" onClick={() => rejectTrial(tc.id)} style={{ marginRight: 8 }} title="Reject">
                                <i className="fa fa-times" aria-hidden="true" style={{ marginRight: 6 }}></i>
                                Reject
                              </button>
                            </>
                          )}
                          {tc.status === 'approved' && (
                            <span style={{ fontSize: '0.9rem', color: '#107a32', marginLeft: 4 }}>Approved</span>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
            <div className="modal-actions" style={{ justifyContent: 'center' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <button className="cancel-btn" onClick={() => setShowTrialModal(false)} title="Close">Close</button>
                <button className="cancel-btn" disabled={trialPage <= 0} onClick={() => { const p = Math.max(0, trialPage - 1); setTrialPage(p); fetchTrialConsents(p); }} title="Previous">Prev</button>
                <button className="save-btn" disabled={(trialPage + 1) * TRIAL_PAGE_SIZE >= trialTotal} onClick={() => { const p = trialPage + 1; setTrialPage(p); fetchTrialConsents(p); }} title="Next">Next</button>
              </div>
            </div>
          </div>
        </div>
      )}
      <table className="tenants-table">
        <thead>
          <tr>
            {user && user.role === 'admin' && (
              <th>
                <input
                  type="checkbox"
                  checked={tenants.filter(t => !Boolean(t.has_active_login)).length > 0 && tenants.filter(t => !Boolean(t.has_active_login)).every(t => selectedTenantIds.includes(getSelectionId(t.id)))}
                  onChange={(e) => {
                    const selectableIds = tenants.filter(t => !Boolean(t.has_active_login)).map(t => getSelectionId(t.id));
                    setSelectedTenantIds(e.target.checked ? selectableIds : []);
                  }}
                />
              </th>
            )}
            <th>S.NO</th>
            <th>Tenant ID</th>
            <th>Name</th>
            <th>Email</th>
            <th>Sector</th>
            <th>Limit</th>
            <th>Start / End Date</th>
            <th>Plan</th>
            {user && user.role === 'admin' && <th>Action</th>}
          </tr>
        </thead>
        <tbody>
          {tenants.map((t, i) => (
            // <tr key={i}>
            <tr key={t.id}>
              {user && user.role === 'admin' ? (
                <td data-label="Select">
                  <input
                    type="checkbox"
                    checked={selectedTenantIds.includes(getSelectionId(t.id))}
                    disabled={Boolean(t.has_active_login)}
                    title={t.has_active_login ? 'Active login detected; this tenant cannot be deleted until logout' : 'Select tenant'}
                    onChange={() => toggleTenantSelection(t.id)}
                  />
                </td>
              ) : null}
              <td data-label="S.NO">{i + 1}</td>
              <td data-label="Tenant ID">{t.id}</td>
              <td data-label="Name">{t.name}</td>
              <td data-label="Email">{t.manager_email}</td>
              <td data-label="Sector">{t.sector_filed || '-'}</td>
              <td data-label="Limit">
                <div>Users: {(t.limit || t.limits) ? `${t.used || 0}/${t.limit || t.limits}` : '-'}</div>
                <div style={{ fontSize: '0.76rem', color: '#64748b', marginTop: '2px' }}>
                  Consent: {t.dpdp_banner_count || 0}/{t.consent_banner_limit || t.limit || t.limits || '-'} | Cookies: {t.cookie_banner_count || 0}/{t.cookie_banner_limit || t.limit || t.limits || '-'}
                </div>
              </td>
              <td data-label="Start / End Date">{(t.start_date || t.startDate) && (t.end_date || t.endDate) ? `${(t.start_date || t.startDate)?.slice(0, 10)} / ${(t.end_date || t.endDate)?.slice(0, 10)}` : '-'}</td>
              <td data-label="Plan">{getPlanLabel(t.plan)}</td>
              {user && user.role === 'admin' ? (
                // <td>
                <td data-label="Action">
                  <span className="action-icon" title="Edit" onClick={() => openEditModal(t)} >
                    <i className="fa fa-edit"></i>
                  </span>
                  <span className="action-icon" title="Select" onClick={() => selectTenant(t)} style={{ color: '#2e7d32' }} >
                    <i className="fa fa-check"></i>
                  </span>
                  {/* <span className="action-icon" title="View Logs" onClick={() => window.open(`/admin/logs?tenant_id=${t.id}`, '_blank')} style={{cursor:'pointer'}}>
                    <i className="fa fa-list"></i>
                  </span> */}
                  {(() => {
                    const statusIcon = getTenantStatusIcon(t);
                    return (
                      <button
                        type="button"
                        className="action-icon"
                        title={statusIcon.title}
                        disabled={!statusIcon.clickable}
                        onClick={() => statusIcon.clickable && toggleTenantStatus(t)}
                        style={{
                          color: statusIcon.color,
                          cursor: statusIcon.clickable ? 'pointer' : 'default',
                          opacity: statusIcon.clickable ? 1 : 0.5,
                          border: 'none',
                          background: 'transparent',
                          padding: 0,
                        }}
                      >
                        <i className={`fa ${statusIcon.icon}`} aria-hidden="true"></i>
                      </button>
                    );
                  })()}
                  <span className="action-icon delete-icon" title="Delete" onClick={() => openDeleteConfirm(t)} style={{ color: '#e53935' }} >
                    <i className="fa fa-trash"></i>
                  </span>
                  <span className="action-icon" title="Mail" onClick={() => handleResendEmail(t)} style={{ cursor: 'pointer' }}><i className="fa fa-envelope"></i></span>
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default TenantsPage;
